/**
 * Floral — Farm-to-Fridge behavior layer.
 * Source: mockups/farm-to-fridge-desktop.html / -mobile.html inline <script>, ported
 * "wrapped as-is" per the overnight-build JS rules for static-page content.
 *
 * Unlike the mockup, this file does NOT build the DOM — floral-farm-to-fridge.liquid
 * server-renders every chapter/step (the copy is immutable, so a client-side data
 * array buys nothing and would hide the content from SEO/no-JS). This file only
 * reproduces the mockup's BEHAVIOR against that pre-rendered markup:
 *  - scroll-linked sticky-image/video crossfade (nearest-to-viewport-center step wins,
 *    same "dwell" technique as the original — no timers)
 *  - chapter bar reveal + active-chapter highlight + progress fill
 *  - chapter-index jump links
 *  - reveal-on-scroll fade-ins
 *  - founder-film lightbox open/close (click, backdrop click, Escape)
 *
 * Desktop-only concerns (sticky crossfade) are skipped harmlessly on mobile since the
 * sticky panes are display:none there (CSS) — querySelectorAll simply finds nothing.
 */
import { getScrollEventTarget, getScrollTop } from '@theme/scroll-container';

(function () {
  const root = document.querySelector('.floral-f2f');
  if (!root) return;

  const steps = [...root.querySelectorAll('[data-f2f-step]')];
  const totalSteps = steps.length;
  const chapBar = root.querySelector('[data-f2f-chapbar]');
  const stepLabel = root.querySelector('[data-f2f-step-label]');
  const progress = root.querySelector('[data-f2f-progress]');

  let ticking = false;
  function evaluate() {
    ticking = false;
    const mid = window.innerHeight / 2;
    let best = null;
    let bestDist = Infinity;
    steps.forEach((s) => {
      const r = s.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const d = Math.abs((r.top + r.bottom) / 2 - mid);
      if (d < bestDist) {
        bestDist = d;
        best = s;
      }
    });
    if (!best) return;

    const ci = best.dataset.f2fStep && best.closest('[data-f2f-chap]')
      ? best.closest('[data-f2f-chap]').dataset.f2fChap
      : null;
    if (ci !== null) {
      const img = root.querySelector('[data-f2f-sticky-img="' + ci + '"]');
      const vid = root.querySelector('[data-f2f-sticky-vid="' + ci + '"]');
      const cap = root.querySelector('[data-f2f-sticky-cap="' + ci + '"]');
      const wantVid = best.dataset.f2fVid;
      if (wantVid) {
        if (vid && vid.getAttribute('src') !== wantVid) {
          vid.src = wantVid;
        }
        if (vid) {
          vid.style.opacity = 1;
          vid.play().catch(() => {});
        }
        if (cap) cap.textContent = best.dataset.f2fCap;
      } else {
        if (vid && vid.style.opacity !== '0') {
          vid.style.opacity = 0;
          vid.pause();
        }
        if (img && img.getAttribute('src') !== best.dataset.f2fImg) {
          img.style.opacity = 0;
          setTimeout(() => {
            img.src = best.dataset.f2fImg;
            if (cap) cap.textContent = best.dataset.f2fCap;
            img.style.opacity = 1;
          }, 160);
        } else if (cap) {
          cap.textContent = best.dataset.f2fCap;
        }
      }
    }

    root.querySelectorAll('[data-f2f-chap-label]').forEach((c) => {
      c.classList.toggle('is-active', c.dataset.f2fChapLabel === ci);
    });
    const idx = best.dataset.f2fStep;
    if (stepLabel) stepLabel.textContent = best.dataset.f2fCap + ' — ' + String(idx).padStart(2, '0') + ' / ' + totalSteps;
    if (progress) progress.style.width = (idx / totalSteps) * 100 + '%';
  }

  // Desktop (≥990px) scrolls inside .page-wrapper, NOT window — bind to the
  // theme's real scroll event target and read its scrollTop, or the chapter
  // bar / sticky crossfade / progress never update on desktop.
  getScrollEventTarget().addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(evaluate);
      }
      if (chapBar) chapBar.classList.toggle('is-on', getScrollTop() > window.innerHeight * 0.85);
    },
    { passive: true }
  );

  root.querySelectorAll('[data-f2f-jump]').forEach((a) => {
    a.addEventListener('click', () => {
      const target = root.querySelector('#floral-f2f-chap-' + a.dataset.f2fJump);
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    });
  });

  const io = new IntersectionObserver(
    (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-in')),
    { threshold: 0.2 }
  );
  root.querySelectorAll('[data-f2f-reveal]').forEach((el) => io.observe(el));

  const vmodal = root.querySelector('[data-f2f-vmodal]');
  const vplayer = root.querySelector('[data-f2f-vmodal-player]');
  const playFilm = root.querySelector('[data-f2f-play-film]');
  const closeBtn = root.querySelector('[data-f2f-vmodal-close]');
  if (playFilm && vmodal && vplayer) {
    playFilm.addEventListener('click', () => {
      vmodal.classList.add('is-open');
      vplayer.play().catch(() => {});
    });
    const close = () => {
      vmodal.classList.remove('is-open');
      vplayer.pause();
    };
    if (closeBtn) closeBtn.addEventListener('click', close);
    vmodal.addEventListener('click', (e) => {
      if (e.target === vmodal) close();
    });
    addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  }

  evaluate();
})();
